import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { reportsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { FileText, Download, ArrowLeft, File } from 'lucide-react';

export default function SavedPdfsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    loadPdfs();
  }, []);

  const loadPdfs = async () => {
    try {
      const res = await reportsApi.listSavedPdfs();
      setPdfs(res.data);
    } catch (error) {
      toast.error('Failed to load saved PDFs');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (reportId, filename) => {
    setDownloading(reportId);
    try {
      const blob = await reportsApi.getSavedPdf(reportId);
      const url = window.URL.createObjectURL(new Blob([blob.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(null);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const goBack = () => {
    const dest = user?.role === 'organisation' ? '/admin' : user?.role === 'supervisor' ? '/supervisor' : '/dashboard';
    navigate(dest);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="saved-pdfs-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Button variant="ghost" size="sm" onClick={goBack} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <h1 className="font-heading text-lg font-bold text-slate-900">Saved PDFs</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <File className="h-5 w-5 text-teal-600" />
              Saved Report PDFs
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pdfs.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>No saved PDFs yet. Save a report PDF to see it here.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Filename</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Report Type</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Saved</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pdfs.map((pdf) => (
                    <TableRow key={pdf.id} data-testid={`pdf-row-${pdf.id}`}>
                      <TableCell className="font-mono text-sm">{pdf.filename}</TableCell>
                      <TableCell>{pdf.client_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{pdf.report_type}</Badge>
                      </TableCell>
                      <TableCell className="text-slate-500">{formatBytes(pdf.file_size)}</TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {new Date(pdf.created_at).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney' })}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(pdf.report_id, pdf.filename)}
                          disabled={downloading === pdf.report_id}
                          data-testid={`download-pdf-${pdf.report_id}`}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          {downloading === pdf.report_id ? 'Downloading...' : 'Download'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
